import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Contact form modal — sends a message to fotr@funontherun.co via the backend
 * Resend integration. Triggered by the footer "Contact" link.
 */
export default function ContactModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill all fields");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name, email, message });
      setSent(true);
      toast.success("Message sent — we'll be in touch soon");
      setTimeout(() => {
        onClose();
        setSent(false);
        setName("");
        setEmail("");
        setMessage("");
      }, 1800);
    } catch (err) {
      toast.error("Something went wrong — try emailing fotr@funontherun.co directly");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          data-testid="contact-modal-backdrop"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-background rounded-3xl shadow-2xl w-full max-w-lg p-6 md:p-8"
            data-testid="contact-modal"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-line transition-colors flex items-center justify-center text-muted"
              aria-label="Close contact form"
              data-testid="contact-modal-close"
            >
              <X size={18} />
            </button>

            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-2">Contact FOTR</h2>
            <p className="text-muted text-sm mb-6">Drop us a note — we read every one and reply within 24 hours.</p>

            {sent ? (
              <div className="py-10 text-center" data-testid="contact-sent-state">
                <div className="w-14 h-14 mx-auto rounded-full bg-primary/15 flex items-center justify-center mb-3">
                  <Send size={22} className="text-primary" />
                </div>
                <div className="font-display font-bold text-lg">Got it — we&rsquo;ll reply soon.</div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
                <input
                  type="text"
                  required
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-line bg-white focus:border-primary outline-none transition-colors"
                  data-testid="contact-name"
                />
                <input
                  type="email"
                  required
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-line bg-white focus:border-primary outline-none transition-colors"
                  data-testid="contact-email"
                />
                <textarea
                  required
                  rows={5}
                  placeholder="What's on your mind?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-line bg-white focus:border-primary outline-none transition-colors resize-none"
                  data-testid="contact-message"
                />
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-ink text-white px-5 py-3 rounded-xl font-bold inline-flex items-center justify-center gap-2 hover:bg-primary transition-colors disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {sending ? "Sending..." : <>Send message <Send size={16} /></>}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
